import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyFaq = { question: "", answer: "", scope: "palais", order: 0, published: true };

const SCOPE_LABELS = {
  palais: "Palais Maasaï",
  comptoir: "Le Comptoir",
  services: "Maasaï Services",
  univers: "Univers Maasaï",
};

export default function FaqManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, order: parseInt(editing.order, 10) || 0 };
      if (editing.id) {
        await api.put(`/admin/faqs/${editing.id}`, payload);
      } else {
        await api.post("/admin/faqs", payload);
      }
      toast.success("Question enregistrée.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/faqs/${id}`);
    toast.success("Question supprimée.");
    load();
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  return (
    <div data-testid="faq-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">FAQ — {items.length} question(s)</h2>
          <p className="mt-1 text-xs text-ivory/40">
            Les questions s'affichent sur la page de l'activité choisie.
          </p>
        </div>
        <button onClick={() => setEditing({ ...emptyFaq })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="faq-add-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter une question
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="faq-edit-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier la question" : "Nouvelle question"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <input required value={editing.question} onChange={set("question")} placeholder="Question" className="input-dark !h-11" data-testid="faq-question-input" />
            <textarea required rows={4} value={editing.answer} onChange={set("answer")} placeholder="Réponse" className="input-dark !h-auto py-3" data-testid="faq-answer-input" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Activité</label>
                <Select value={editing.scope} onValueChange={(v) => setEditing((s) => ({ ...s, scope: v }))}>
                  <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="faq-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-coal text-ivory">
                    {Object.entries(SCOPE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Ordre</label>
                <input type="number" value={editing.order} onChange={set("order")} className="input-dark !h-11" data-testid="faq-order-input" />
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm text-ivory/70">
              Publiée
              <Switch checked={editing.published} onCheckedChange={(v) => setEditing((s) => ({ ...s, published: v }))} data-testid="faq-published-switch" />
            </label>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="faq-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="faq-empty">
          Aucune question pour le moment. Créez votre première FAQ.
        </p>
      ) : (
        <div className="space-y-2" data-testid="faq-list">
          {items.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`faq-row-${f.id}`}>
              <span className={`min-w-0 flex-1 truncate text-sm ${f.published ? "text-ivory" : "text-ivory/35 line-through"}`}>
                {f.question}
              </span>
              <span className="rounded border border-gold/30 px-2 py-0.5 text-[11px] font-semibold text-gold">
                {SCOPE_LABELS[f.scope] || f.scope}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing({ ...f })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`faq-edit-${f.id}`}>
                  <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button onClick={() => remove(f.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`faq-delete-${f.id}`}>
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
